import { ObjectID } from "bson";
import pick from "lodash/pick";
import { create } from "zustand";
import { toast } from "~served/ui-comps/toast";
import { getCurrentLocationCache } from "~/queries/use-get-current-location";
import { getCurrentVenueCache } from "~/queries/use-get-current-venue";
import { CreateOrderItemInput } from "~/types/__generated/gql/graphql";
import { Actions, State } from "~/types/store";
import { createSelectors } from "~/types/utils";
import {
	applyServiceChargeBdToOrder,
	applyServiceChargeToOrder,
	applyTaxToOrder,
	applyVatToOrder,
} from "~served/utils/calculation";

export const ALL_CATEGORY = "all";

const assertCachedVenue = () => {
	const venue = getCurrentVenueCache();
	if (!venue) {
		toast.error("Venue not found. Try refreshing the page");
		throw new Error("Venue not found");
	}
	return venue;
};

const assertCachedLocation = () => {
	const location = getCurrentLocationCache();
	if (!location) {
		toast.error("Location not found. Try refreshing the page");
		throw new Error("Location not found");
	}
	return location;
};

const getPricingContext = (
	venue = assertCachedVenue(),
	location = assertCachedLocation(),
) => {
	const vat = location.no_vat ? 0 : venue.vat;
	const defaultType = venue.default_service_charge_type ?? "ad";
	const canApplyAd =
		!location.no_service_charge && (venue.service_charge ?? 0) > 0;
	const canApplyBd =
		!location.no_service_charge && (venue.service_charge_bd ?? 0) > 0;
	const serviceCharge = (
		defaultType === "ad" ? canApplyAd : canApplyAd && !canApplyBd
	)
		? venue.service_charge
		: 0;
	const serviceChargeBd = (
		defaultType === "bd" ? canApplyBd : canApplyBd && !canApplyAd
	)
		? (venue.service_charge_bd ?? 0)
		: 0;

	return {
		tax: venue.tax ?? 0,
		vat,
		serviceCharge,
		serviceChargeBd,
	};
};

export const repriceCart = (
	cart: State["cart"],
	venue = assertCachedVenue(),
	location = assertCachedLocation(),
) => {
	const { tax, vat, serviceCharge, serviceChargeBd } = getPricingContext(
		venue,
		location,
	);

	const { items: itemsWithVat } = applyVatToOrder({
		order: { items: cart.items },
		vat,
	});
	const { items: itemsWithServiceCharge } = applyServiceChargeToOrder({
		order: { items: itemsWithVat },
		serviceCharge,
	});
	const { items: itemsWithServiceChargeBd } = applyServiceChargeBdToOrder({
		order: { items: itemsWithServiceCharge } as any,
		serviceChargeBd,
	});
	const { items: itemsWithTax } = applyTaxToOrder({
		order: { items: itemsWithServiceChargeBd },
		tax,
	});

	return {
		...cart,
		items: itemsWithTax as CreateOrderItemInput[],
	};
};

const INITIAL_STATE: State = {
	isSessionExpired: false,
	isReady: false,
	isConnectedToServer: true,
	isNeededHelp: false,
	token: null,
	venueId: null,
	locationId: null,
	menuId: null,
	orderRef: null,
	orderId: null,
	deviceId: null,
	cart: {
		customer: undefined,
		payment_types: [],
		items: [],
		idempotency_key: "",
	},
	paywayPaymentType: "aba",
	search: "",
	activeDigitalMenuLanguage: "en",
	selectedCategoryId: ALL_CATEGORY,
	selectedItemTags: [],
};

export const useStore = create<State & Actions>()((set, get) => ({
	...INITIAL_STATE,
	setIsSessionExpired: (data) => set({ isSessionExpired: data }),
	setIsReady: (data) => set({ isReady: data }),
	setIsConnectedToServer: (data) => set({ isConnectedToServer: data }),
	setIsNeededHelp: (data) => set({ isNeededHelp: data }),
	setDeviceId: (data) => set({ deviceId: data }),
	updateCart: (data) => {
		set({ cart: repriceCart({ ...get().cart, ...data }) });
	},
	addItem: (item) => {
		const prevItems = get().cart.items;
		const itemInCart = prevItems.find(
			(ci) => ci.item === item._id && !ci.options.length,
		);

		const newItems = itemInCart
			? prevItems.map((ci) =>
					ci._id === itemInCart._id
						? { ...itemInCart, quantity: itemInCart.quantity + 1 }
						: ci,
				)
			: [
					...prevItems,
					{
						...pick(item, [
							"item_id",
							"cost",
							"title",
							"title_localized",
							"prep_time",
							"type",
							"original_price",
							"minimum_required_price",
							"original_price_addons",
							"listed_price",
							"extra_quantity",
							"category",
							"printer_tag",
							"no_vat",
							"no_service_charge",
							"is_open_item",
							"is_hide_from_receipt",
						]),
						_id: new ObjectID().toString(),
						item: item._id,
						recipe: item.recipe,
						quantity: 1,
						options: [],
					},
				];

		set((s) => ({
			cart: {
				...repriceCart({
					...s.cart,
					items: newItems as CreateOrderItemInput[],
				}),
			},
		}));
	},
	removeItem: (item) => {
		const prevItems = get().cart.items;
		const itemInCart =
			prevItems.find((ci) => ci.item === item._id && !ci.options.length) ??
			prevItems.find((ci) => ci.item === item._id);

		if (!itemInCart) return;

		const newItems = prevItems
			.map((ci) =>
				ci._id === itemInCart._id
					? { ...itemInCart, quantity: itemInCart.quantity - 1 }
					: ci,
			)
			.filter((i) => i.quantity);

		set((s) => ({
			cart: {
				...repriceCart({
					...s.cart,
					items: newItems,
				}),
			},
		}));
	},
	setPaywayPaymentType: (data) => set({ paywayPaymentType: data }),
	resetCart: () => set({ cart: INITIAL_STATE.cart }),
	setSearch: (data) => set({ search: data }),
	setActiveDigitalMenuLanguage: (data) =>
		set({ activeDigitalMenuLanguage: data }),
	setSelectedCategoryId: (data) => set({ selectedCategoryId: data }),
	setSelectedItemTags: (data) => set({ selectedItemTags: data }),
}));

export const {
	use: useSelector,
	setState,
	getState,
} = createSelectors(useStore);

export const resetCurrentCart = () => {
	setState({ cart: INITIAL_STATE.cart });
};

export const recalculateCurrentCart = () => {
	const { cart } = getState();
	if (!cart.items.length) return;
	setState({ cart: repriceCart(cart) });
};

export const useIsReady = () => {
	return useSelector.isReady();
};
export const useSetIsReady = () => {
	return useSelector.setIsReady();
};
export const useIsConnectedToServer = () => {
	return useSelector.isConnectedToServer();
};
export const useSetIsConnectedToServer = () => {
	return useSelector.setIsConnectedToServer();
};
export const useIsNeededHelp = () => {
	return useSelector.isNeededHelp();
};
export const useSetIsNeededHelp = () => {
	return useSelector.setIsNeededHelp();
};
export const useToken = () => {
	return useSelector.token();
};
export const useVenueId = () => {
	return useSelector.venueId();
};
export const useLocationId = () => {
	return useSelector.locationId();
};
export const useMenuId = () => {
	return useSelector.menuId();
};
export const useOrderRef = () => {
	return useSelector.orderRef();
};
export const useOrderId = () => {
	return useSelector.orderId();
};
export const useDeviceId = () => {
	return useSelector.deviceId();
};
export const useSetDeviceId = () => {
	return useSelector.setDeviceId();
};
export const useUpdateCart = () => {
	return useSelector.updateCart();
};
export const useAddItem = () => {
	return useSelector.addItem();
};
export const useRemoveItem = () => {
	return useSelector.removeItem();
};
export const useCart = () => {
	return useSelector.cart();
};
export const useResetCart = () => {
	return useSelector.resetCart();
};
export const usePaywayPaymentType = () => {
	return useSelector.paywayPaymentType();
};
export const useSetPaywayPaymentType = () => {
	return useSelector.setPaywayPaymentType();
};
export const useSetIsSessionExpired = () => {
	return useSelector.setIsSessionExpired();
};
export const useIsSessionExpired = () => {
	return useSelector.isSessionExpired();
};
export const useCartItems = () => {
	return useStore((s) => s.cart.items);
};

export const useSelectedItemTags = () => {
	return useStore((s) => s.selectedItemTags);
};

export const useActiveDigitalMenuLanguage = () => {
	return useSelector.activeDigitalMenuLanguage();
};

export const useSetActiveDigitalMenuLanguage = () => {
	return useSelector.setActiveDigitalMenuLanguage();
};

export const useSetSelectedItemTags = () => {
	return useSelector.setSelectedItemTags();
};
